import React, { useState } from 'react';
import { FORMS } from '../../schemas/stepMap';
import FormBuilder from '../../components/formBuilder';
import { Helmet } from 'react-helmet';
import { useProperty } from '../../hooks/useProperty';
import BackForwardButtons from '../../components/buttons/BackForwardButtons';
import { useSteps } from '../../hooks/useSteps';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import PropertyPageHeader from '../../components/PropertyPageHeader';
import FlipKeyMetrics from './FlipKeyMetrics';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import toast from 'react-hot-toast';
import formatAddress from '../../utility/formatAddress';
import pdfIcon from '../../assets/pdf.svg';
import Tooltip from '../../components/Tooltip';
import getCurrentDateFormatted from '../../utility/dates';
import { Icon, Logo } from '../../components';

export default function FlipSummary() {
  const { propertyData } = useProperty();
  const { prev, next } = useSteps('flip');
  const purchaseWorksheet = FORMS.flipAnalysis;
  const purchaseWorksheetSchema = purchaseWorksheet.schema;

  const { pathname } = useLocation();
  const sharing = pathname.includes('sharing');

  const [printing, setPrinting] = useState(false);

  function printPDF() {
    setPrinting(true);
    setTimeout(() => {
      generate();
    }, 0);
  }

  function generate() {
    setPrinting(true);
    const element = document.getElementById('page-summary');

    if (!element) {
      setPrinting(false);
      return;
    }

    try {
      const clonedElement = element.cloneNode(true);
      clonedElement.style.position = 'absolute';
      clonedElement.style.top = '-9999px';
      clonedElement.style.left = '-9999px';
      clonedElement.classList.add('printing');
      clonedElement.classList.add('p-8');
      clonedElement.style.width = '1080px';
      const h = document.getElementById('printable-header');

      const header = h.cloneNode(true);

      header.classList.remove('hidden');
      header.classList.add('grid');
      clonedElement.insertBefore(header, clonedElement.firstChild);

      document.body.appendChild(clonedElement);

      html2canvas(clonedElement, {
        scale: 2,
      }).then((canvas) => {
        const componentWidth = clonedElement.offsetWidth;
        const componentHeight = clonedElement.offsetHeight;

        document.body.removeChild(clonedElement);
        const orientation = componentWidth >= componentHeight ? 'l' : 'p';

        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
          orientation,
          unit: 'px',
        });

        pdf.internal.pageSize.width = componentWidth;
        pdf.internal.pageSize.height = componentHeight;

        pdf.addImage(imgData, 'PNG', 0, 0, componentWidth, componentHeight);

        const fileName = `${
          propertyData.propertyName
        } - Flip Analysis - ${getCurrentDateFormatted()}`;

        pdf.save(`${fileName}.pdf`);
        toast.success('PDF downloaded', { position: 'bottom-right' });
        setPrinting(false);
        element.style.width = 'auto';
      });
    } catch (error) {
      console.log('Failed to print the PDF', error);
      setPrinting(false);
      element.style.width = 'auto';
      toast.error('Something went wrong', { position: 'bottom-right' });
    }
  }

  if (!propertyData.flipWorksheetSubmitted) {
    return (
      <div className="w-full flex flex-col items-center justify-center mt-36">
        <h2 className="mb-6 text-xl font-medium text-primary">
          No Flip Analysis Created
        </h2>
        {!sharing && (
          <p className="mb-72 w-2/3 text-center">
            Once you add property details in the{' '}
            <Link
              to="../flip-worksheet"
              className="font-medium text-primary hover:text-primary-700 focus:text-primary-700"
            >
              flip worksheet
            </Link>
            , you will be able to see your property analysis here.
          </p>
        )}
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>PropertyCast.io - Purchase Analysis</title>
      </Helmet>
      <section>
        <div id="printable-header" className="grid-cols-4 p-4 hidden mb-10">
          <div className="flex items-center justify-start">
            <Logo variant="dark" size="md" />
          </div>
          <div className="col-span-2 flex items-center justify-center">
            <h1 className="text-center text-4xl font-medium font-serif text-neutral-900">
              {propertyData.propertyName} - Flip Analysis
            </h1>
          </div>
          <div className="text-right text-md">
            <p>PropertyCast.io</p>
            <p>{formatAddress(propertyData.propertyAddress, 'short')}</p>
            <br />
            <p>{getCurrentDateFormatted()}</p>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <PropertyPageHeader
            title={purchaseWorksheetSchema.title}
            description={purchaseWorksheetSchema.description}
          >
            <Tooltip
              delay={true}
              text="Download the current summary in PDF format."
            >
              <button
                className="transition inline-flex justify-center gap-[4px] items-center h-[39px] py-1.5 px-2  border border-transparent  text-md font-medium rounded-md text-white bg-primary hover:bg-primary-500 focus:bg-primary-500 focus:outline-none disabled:bg-neutral-200 disabled:cursor-not-allowed"
                onClick={printPDF}
              >
                <span className="sr-only">Download PDF</span>
                {printing ? (
                  <Icon
                    icon="BiLoaderAlt"
                    className="text-xl animate-spin h-5 w-5"
                  ></Icon>
                ) : (
                  <img
                    className="h-5 w-5 text-white"
                    src={pdfIcon}
                    alt="pdf icon"
                  />
                )}
              </button>
            </Tooltip>
          </PropertyPageHeader>
        </div>
        <div>
          {/* {user && !userDocData.loading && ( // no investment metrics for flips yet 
            <InvestmentMetrics
              schema={purchaseWorksheet.investmentMetrics}
              data={{ ...userDocData, ...propertyData }}
            />
          )} */}
        </div>
        <div id="page-summary" className=" bg-primary-50">
          <div className="mb-10">
            <FlipKeyMetrics propertyData={propertyData} />
          </div>
          <div className="pb-2">
            <FormBuilder
              formType={purchaseWorksheet.formType}
              calculationsSchema={purchaseWorksheetSchema}
              beforeData={propertyData}
            />
          </div>
        </div>
        <div className="pt-4">
          <BackForwardButtons prev={prev} next={next} />
        </div>
      </section>
    </>
  );
}
